import React from 'react';
import { Chip, Tooltip, Stack } from '@mui/material';

/**
 * App-version cell, shared by the driver and user lists and both detail pages.
 *
 * PER-BINARY, not per-user. `users` carries a separate versionCode for each app, because they are
 * independent release sequences — a driver who opens the rider app used to overwrite their driver
 * version, which silently dropped them out of location-ping eligibility. This component must read the
 * column matching the row's role, NOT the legacy shared `appVersion`, or the UI will contradict the
 * gate: showing a rider build while the backend correctly pings on the driver build.
 *
 * What the states mean:
 *   NULL       → "Old app". No version reported for this binary, so it is excluded from the ping
 *                rails (a ping would render a junk notification on it). Amber: the cohort to shrink.
 *   >= gate    → green. Probe-eligible today. DRIVERS ONLY — riders are never pinged, so their
 *                versions are never gate-coloured, which would imply a behaviour that does not exist.
 *   < gate     → plain. Reports a version but is behind the gate.
 *
 * The secondary chip appears only for dual-app users, so the other binary's build is visible rather
 * than hidden — that is exactly the population where the two numbers diverge and confuse people.
 */
interface Props {
  driverAppVersion?: number | null;
  riderAppVersion?: number | null;
  /** Decides which binary is primary for this row. Anything not 'rider' is treated as a driver. */
  role?: string | null;
  lastSeen?: string | null;
  /** Live LOCATION_PING_MIN_APP_VERSION. 0 = gate off, so no eligibility colouring is implied. */
  gate?: number;
}

const fmtLastSeen = (iso?: string | null): string => {
  if (!iso) return 'never reported a token';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return 'unknown';
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) return 'last seen today';
  if (days === 1) return 'last seen yesterday';
  return `last seen ${days} days ago`;
};

const AppVersionCell: React.FC<Props> = ({
  driverAppVersion, riderAppVersion, role, lastSeen, gate = 0,
}) => {
  const isRider = role === 'rider';
  const primary = isRider ? riderAppVersion : driverAppVersion;
  const secondary = isRider ? driverAppVersion : riderAppVersion;
  const secondaryLabel = isRider ? 'driver app' : 'rider app';
  const seen = fmtLastSeen(lastSeen);

  const primaryChip = (() => {
    if (primary == null) {
      return (
        <Tooltip title={`No ${isRider ? 'rider' : 'driver'} app version reported — excluded from location pings. ${seen}.`}>
          <Chip label="Old app" size="small" color="warning" variant="outlined" />
        </Tooltip>
      );
    }
    // Eligibility is a driver-only concept; colouring a rider build green would imply it gets pinged.
    const eligible = !isRider && gate > 0 && primary >= gate;
    const title = isRider
      ? `Rider app build ${primary}. Riders are never sent location pings. ${seen}.`
      : gate > 0
        ? `${eligible ? 'Probe-eligible' : `Below the gate (${gate})`}. ${seen}.`
        : `${seen}.`;
    return (
      <Tooltip title={title}>
        <Chip label={primary} size="small" color={eligible ? 'success' : 'default'} variant="outlined" />
      </Tooltip>
    );
  })();

  if (secondary == null) return primaryChip;

  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {primaryChip}
      <Tooltip title={`Also uses the ${secondaryLabel}, build ${secondary}. Tracked separately — it cannot affect ping eligibility.`}>
        <Chip label={`${isRider ? 'D' : 'R'}:${secondary}`} size="small" variant="outlined"
              sx={{ opacity: 0.65, fontSize: 11 }} />
      </Tooltip>
    </Stack>
  );
};

export default AppVersionCell;
